import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { Loader2, Download, Save, CalendarCheck, Users, FileText } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, downloadCsv, cx } from "@/lib/api";

const CYCLE = ["", "presente", "mezza", "ferie", "malattia", "assente"];
const META = {
  presente: { code: "P", ore: 8, cls: "bg-emerald-500 text-white border-emerald-600", label: "Presente" },
  mezza: { code: "½", ore: 4, cls: "bg-amber-400 text-slate-900 border-amber-500", label: "Mezza giornata" },
  ferie: { code: "F", ore: 0, cls: "bg-blue-500 text-white border-blue-600", label: "Ferie" },
  malattia: { code: "M", ore: 0, cls: "bg-violet-500 text-white border-violet-600", label: "Malattia" },
  assente: { code: "A", ore: 0, cls: "bg-red-500 text-white border-red-600", label: "Assente" },
};
const WD = ["D", "L", "M", "M", "G", "V", "S"];
const curMonth = () => new Date().toISOString().slice(0, 7);

export default function Presenze() {
  const [cantieri, setCantieri] = useState([]);
  const [operai, setOperai] = useState([]);
  const [extraRec, setExtraRec] = useState({}); // operai eliminati presenti nello storico
  const [cantiereId, setCantiereId] = useState("");
  const [mese, setMese] = useState(curMonth());
  const [cells, setCells] = useState({}); // `${operaioId}|${YYYY-MM-DD}` -> stato
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/cantieri"), api.get("/operai")]).then(([c, o]) => {
      const aperti = c.filter((x) => x.stato === "attivo");
      setCantieri(aperti.length ? aperti : c);
      setOperai(o);
      if ((aperti.length ? aperti : c)[0]) setCantiereId((aperti.length ? aperti : c)[0].id);
    }).catch(() => {});
  }, []);

  const days = useMemo(() => {
    const [y, m] = mese.split("-").map(Number);
    const n = new Date(y, m, 0).getDate();
    return Array.from({ length: n }, (_, i) => {
      const dd = String(i + 1).padStart(2, "0");
      const date = `${mese}-${dd}`;
      const wd = new Date(`${date}T00:00:00`).getDay();
      return { d: i + 1, date, wd, weekend: wd === 0 || wd === 6 };
    });
  }, [mese]);

  const load = useCallback(async () => {
    if (!cantiereId || !mese) return;
    setLoading(true);
    try {
      const data = await api.get("/presenze", { cantiere_id: cantiereId, mese });
      const map = {};
      const extra = {};
      (data.records || []).forEach((r) => {
        map[`${r.operaio_id}|${r.data}`] = r.stato;
        if (r.operaio_nome) extra[r.operaio_id] = r.operaio_nome;
      });
      setCells(map);
      setExtraRec(extra);
    } catch { toast.error("Caricamento presenze non riuscito."); }
    setLoading(false);
  }, [cantiereId, mese]);

  useEffect(() => { load(); }, [load]);

  const allOperai = [
    ...operai,
    ...Object.entries(extraRec)
      .filter(([id]) => !operai.some((o) => o.id === id))
      .map(([id, nome]) => ({ id, nome, inactive: true })),
  ];

  const cycle = (operaioId, date) => {
    const key = `${operaioId}|${date}`;
    setCells((c) => {
      const cur = c[key] || "";
      const next = CYCLE[(CYCLE.indexOf(cur) + 1) % CYCLE.length];
      const nc = { ...c };
      if (next) nc[key] = next; else delete nc[key];
      return nc;
    });
  };

  const totals = (operaioId) => {
    let giorni = 0, ore = 0, ferie = 0, malattia = 0, assenze = 0;
    days.forEach(({ date }) => {
      const st = cells[`${operaioId}|${date}`];
      if (!st) return;
      ore += META[st].ore;
      if (st === "presente" || st === "mezza") giorni += 1;
      else if (st === "ferie") ferie += 1;
      else if (st === "malattia") malattia += 1;
      else if (st === "assente") assenze += 1;
    });
    return { giorni, ore, ferie, malattia, assenze };
  };

  const save = async () => {
    if (!cantiereId) return toast.error("Seleziona un cantiere.");
    const records = Object.entries(cells).map(([k, stato]) => {
      const [operaio_id, data] = k.split("|");
      return { operaio_id, data, stato };
    });
    setSaving(true);
    try {
      await api.post("/presenze/bulk", { cantiere_id: cantiereId, mese, records });
      toast.success("Presenze salvate.");
    } catch (err) { toast.error(err.message || "Salvataggio non riuscito."); }
    setSaving(false);
  };

  const exportCsv = async () => {
    try {
      await downloadCsv("/presenze/export", `presenze_${mese}.csv`, { cantiere_id: cantiereId, mese });
      toast.success("Report esportato in CSV.");
    } catch { toast.error("Esportazione non riuscita."); }
  };

  const exportPdf = async () => {
    try {
      await downloadCsv("/presenze/export-pdf", `presenze_${mese}.pdf`, { cantiere_id: cantiereId, mese });
      toast.success("Report PDF generato.");
    } catch { toast.error("Esportazione PDF non riuscita."); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2"><CalendarCheck className="h-7 w-7 text-amber-600" /> Presenze Operai</h1>
          <p className="text-sm text-muted-foreground mt-1">Registra giorno per giorno la presenza di ogni operaio e genera il report mensile.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportCsv} disabled={!cantiereId} data-testid="presenze-export-button" className="gap-2"><Download className="h-4 w-4" /> Report CSV</Button>
          <Button variant="outline" onClick={exportPdf} disabled={!cantiereId} data-testid="presenze-export-pdf-button" className="gap-2"><FileText className="h-4 w-4" /> Report PDF</Button>
          <Button onClick={save} disabled={saving || !cantiereId} data-testid="presenze-save-button"
            className="gap-2 bg-slate-900 hover:bg-slate-800 dark:bg-amber-500 dark:hover:bg-amber-600 dark:text-slate-900">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salva
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-4">
        <div className="space-y-1.5 min-w-[220px]">
          <Label>Cantiere</Label>
          <Select value={cantiereId} onValueChange={setCantiereId}>
            <SelectTrigger data-testid="presenze-cantiere-select"><SelectValue placeholder="Seleziona cantiere..." /></SelectTrigger>
            <SelectContent>
              {cantieri.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Mese</Label>
          <Input type="month" value={mese} onChange={(e) => setMese(e.target.value)} data-testid="presenze-mese-input" className="w-[170px]" />
        </div>
        <div className="flex flex-wrap gap-2 text-xs">
          {Object.entries(META).map(([k, m]) => (
            <span key={k} className="flex items-center gap-1">
              <span className={cx("inline-flex h-5 w-5 items-center justify-center rounded border text-[11px] font-bold", m.cls)}>{m.code}</span>
              {m.label}
            </span>
          ))}
        </div>
      </div>

      {allOperai.length === 0 ? (
        <Card><CardContent className="py-16 text-center">
          <Users className="mx-auto h-10 w-10 text-muted-foreground" />
          <p className="mt-4 font-medium">Nessun operaio in archivio</p>
          <p className="text-sm text-muted-foreground">Aggiungi gli operai in Archivio per registrarne le presenze.</p>
        </CardContent></Card>
      ) : loading ? (
        <div className="h-64 rounded-xl bg-secondary animate-pulse" />
      ) : (
        <Card data-testid="presenze-grid">
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr>
                  <th className="sticky left-0 z-10 bg-card border-b border-r px-3 py-2 text-left font-medium min-w-[160px]">Operaio</th>
                  {days.map((d) => (
                    <th key={d.d} className={cx("border-b px-0.5 py-1 text-center font-medium min-w-[30px]", d.weekend && "bg-secondary/60 text-muted-foreground")}>
                      <div className="text-[10px] leading-none">{WD[d.wd]}</div>
                      <div className="text-xs">{d.d}</div>
                    </th>
                  ))}
                  <th className="border-b border-l px-2 py-2 text-center font-medium">GG</th>
                  <th className="border-b px-2 py-2 text-center font-medium">Ore</th>
                </tr>
              </thead>
              <tbody>
                {allOperai.map((o) => {
                  const t = totals(o.id);
                  return (
                    <tr key={o.id} className="hover:bg-secondary/30">
                      <td className="sticky left-0 z-10 bg-card border-b border-r px-3 py-1.5 font-medium whitespace-nowrap">{o.nome}{o.inactive && <span className="ml-1 text-[10px] text-muted-foreground">(non più attivo)</span>}</td>
                      {days.map((d) => {
                        const st = cells[`${o.id}|${d.date}`];
                        const m = st ? META[st] : null;
                        return (
                          <td key={d.d} className={cx("border-b p-0.5 text-center", d.weekend && "bg-secondary/40")}>
                            <button
                              data-testid={`presenza-cell-${o.id}-${d.d}`}
                              onClick={() => cycle(o.id, d.date)}
                              className={cx(
                                "h-7 w-7 rounded border text-[11px] font-bold transition-colors",
                                m ? m.cls : "border-dashed border-border text-transparent hover:bg-secondary"
                              )}>
                              {m ? m.code : "·"}
                            </button>
                          </td>
                        );
                      })}
                      <td className="border-b border-l px-2 py-1.5 text-center font-mono font-semibold">{t.giorni}</td>
                      <td className="border-b px-2 py-1.5 text-center font-mono text-muted-foreground">{t.ore}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {allOperai.length > 0 && !loading && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Report Mensile — {mese}</CardTitle></CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-muted-foreground">
                  <th className="py-2 text-left font-medium">Operaio</th>
                  <th className="py-2 text-right font-medium">Giorni Lavorati</th>
                  <th className="py-2 text-right font-medium">Ore Totali</th>
                  <th className="py-2 text-right font-medium">Ferie</th>
                  <th className="py-2 text-right font-medium">Malattia</th>
                  <th className="py-2 text-right font-medium">Assenze</th>
                </tr>
              </thead>
              <tbody>
                {allOperai.map((o) => {
                  const t = totals(o.id);
                  return (
                    <tr key={o.id} className="border-b last:border-0" data-testid={`presenze-report-row-${o.id}`}>
                      <td className="py-2 font-medium">{o.nome}{o.inactive && <span className="ml-1 text-[10px] text-muted-foreground">(non più attivo)</span>}</td>
                      <td className="py-2 text-right font-mono">{t.giorni}</td>
                      <td className="py-2 text-right font-mono">{t.ore}</td>
                      <td className="py-2 text-right font-mono">{t.ferie}</td>
                      <td className="py-2 text-right font-mono">{t.malattia}</td>
                      <td className="py-2 text-right font-mono">{t.assenze}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
